import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BookOpen, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { pagesDuLivre } from './textesSeptembre';
import { supportDuTexte } from '../../generators/evalnat/formesParalleles';
import { genreDuSupport } from '../../generators/evalnat/supports';

/**
 * Le livre du quiz en MODE LECTURE (12/10/2026) : le texte de compréhension en plein écran, sur une vraie page.
 * Passé à l'épreuve de Musk : ce qui aide à lire, rien d'autre (ni page qui se plie en 3D, ni sons, ni texture,
 * ni minuterie).
 * - Plein écran sur fond sombre ; en haut le titre, la taille du texte (A− / A+, retenue pour l'élève) et la croix.
 * - Une page couleur papier, environ 65 caractères par ligne, alignée à gauche ; police à empattements pour un
 *   texte littéraire, la police habituelle pour un document.
 * - Tourner les pages : les flèches sur les côtés, les flèches du clavier, le glissement du doigt ; une barre de
 *   progression et « Page 2 sur 6 ». Un seul ascenseur : celui de la page.
 * - À l'ouverture imposée, le grand bouton dit « Page suivante » tant que l'élève n'a pas atteint la dernière
 *   page, puis « J'ai lu le texte, je commence ». La croix ferme toujours.
 * - Une image s'agrandit au clic et tient dans l'écran.
 * Le seul livre de l'appli depuis le 12/10/2026 : l'élève dans le quiz, le prof par « Lire le texte »
 * (FenetreTexteSupport, au-dessus des fenêtres du composeur : auDessus). L'ancien (QuestionSpecifiqueFR) est supprimé.
 */
const TAILLES = ['text-[17px]', 'text-[19px]', 'text-[22px]'];
const CLE_TAILLE = 'tanoed.livre.taille';

const lireTaille = () => {
  try {
    const t = parseInt(window.localStorage.getItem(CLE_TAILLE), 10);
    return t >= 0 && t < TAILLES.length ? t : 1;
  } catch {
    return 1;
  }
};

const nomDuTitre = (titre, typeLabel) => titre
  || String(typeLabel || '').replace(/^Compréhension de l['’]écrit\s*/i, '').replace(/^Document\s+/i, '')
  || 'Texte';

const BoutonRond = ({ label, onClick, disabled, children, className = '' }) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    disabled={disabled}
    className={`flex items-center justify-center rounded-full text-slate-200 transition hover:bg-white/15 disabled:opacity-30 disabled:hover:bg-transparent ${className}`}
  >
    {children}
  </button>
);

const LivreImmersif = ({
  titre, typeLabel, niveau, supportId, pages: pagesDonnees, lectureImposee = false, onFermer, auDessus = false,
}) => {
  const pages = useMemo(() => pagesDuLivre({ niveau, typeLabel, pages: pagesDonnees }), [niveau, typeLabel, pagesDonnees]);
  const total = Math.max(pages.length, 1);
  const [page, setPage] = useState(1);
  const [vuFin, setVuFin] = useState(total <= 1);
  const [taille, setTaille] = useState(lireTaille);
  const [entree, setEntree] = useState(true);
  const [agrandie, setAgrandie] = useState(null);
  const zonePage = useRef(null);
  const toucher = useRef(null);

  // Le genre du texte : par son id (variante, forme parallèle), par son type (septembre), sinon par son surtitre
  const documentaire = genreDuSupport(supportId) === 'documentaire'
    || supportDuTexte(typeLabel) === 'documentaire'
    || pages.some((p) => /documentaire/i.test(p.surtitre || ''));
  const donnee = pages[page - 1];

  const suivante = useCallback(() => setPage((p) => Math.min(p + 1, total)), [total]);
  const precedente = useCallback(() => setPage((p) => Math.max(p - 1, 1)), []);

  // À chaque page : retour en haut, entrée en douceur, et la dernière page atteinte une fois pour toutes
  useEffect(() => {
    if (page >= total) setVuFin(true);
    if (zonePage.current) zonePage.current.scrollTop = 0;
    setEntree(false);
    const id = requestAnimationFrame(() => setEntree(true));
    return () => cancelAnimationFrame(id);
  }, [page, total]);

  // Le clavier : flèches pour tourner les pages, Échap pour fermer (l'image agrandie d'abord). Écouté avant tout
  // le reste et arrêté là : la page derrière (le quiz, la fenêtre de modification du prof) ne réagit pas.
  useEffect(() => {
    const touche = (e) => {
      if (!['ArrowRight', 'ArrowLeft', 'Escape'].includes(e.key)) return;
      e.stopPropagation();
      if (agrandie) {
        if (e.key === 'Escape') setAgrandie(null);
        return;
      }
      if (e.key === 'ArrowRight') suivante();
      else if (e.key === 'ArrowLeft') precedente();
      else onFermer();
    };
    window.addEventListener('keydown', touche, true);
    return () => window.removeEventListener('keydown', touche, true);
  }, [agrandie, suivante, precedente, onFermer]);

  const changerTaille = (delta) => {
    const nouvelle = Math.min(Math.max(taille + delta, 0), TAILLES.length - 1);
    setTaille(nouvelle);
    try {
      window.localStorage.setItem(CLE_TAILLE, String(nouvelle));
    } catch {
      // stockage indisponible : la taille vaut pour cette lecture seulement
    }
  };

  // Le doigt : un glissement franc à l'horizontale tourne la page
  const debutToucher = (e) => {
    const t = e.touches[0];
    toucher.current = { x: t.clientX, y: t.clientY };
  };
  const finToucher = (e) => {
    const depart = toucher.current;
    toucher.current = null;
    if (!depart) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - depart.x;
    const dy = t.clientY - depart.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > 1.5 * Math.abs(dy)) {
      if (dx < 0) suivante();
      else precedente();
    }
  };

  const bouton = lectureImposee && !vuFin
    ? { libelle: 'Page suivante', action: suivante, fleche: true }
    : { libelle: lectureImposee ? "J'ai lu le texte, je commence" : 'Revenir à la question', action: onFermer, fleche: false };

  const image = (img, cle) => (
    <button
      key={cle}
      type="button"
      onClick={() => setAgrandie(img)}
      className="block w-full cursor-zoom-in rounded-md bg-white p-1 ring-1 ring-stone-200 transition hover:ring-stone-400"
      aria-label={`Agrandir l'image : ${img.alt || 'image du texte'}`}
    >
      <img src={img.src} alt={img.alt || ''} className="mx-auto max-h-[45vh] w-auto max-w-full object-contain" />
    </button>
  );

  return (
    <div className={`fixed inset-0 ${auDessus ? 'z-[10005]' : 'z-50'} flex flex-col bg-slate-950/95 backdrop-blur-sm`} role="dialog" aria-modal="true" aria-label={`Lecture : ${nomDuTitre(titre, typeLabel)}`}>
      {/* En haut : le texte, la taille des lettres, la croix */}
      <div className="flex items-center gap-3 px-4 py-3 sm:px-6">
        <BookOpen className="h-5 w-5 flex-shrink-0 text-cyan-400" />
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{documentaire ? 'Texte documentaire' : 'Texte littéraire'}</p>
          <h2 className="truncate font-semibold text-slate-100">{nomDuTitre(titre, typeLabel)}</h2>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <BoutonRond label="Texte plus petit" onClick={() => changerTaille(-1)} disabled={taille === 0} className="h-10 w-10 text-sm font-bold">A−</BoutonRond>
          <BoutonRond label="Texte plus grand" onClick={() => changerTaille(1)} disabled={taille === TAILLES.length - 1} className="h-10 w-10 text-lg font-bold">A+</BoutonRond>
          <BoutonRond label="Fermer le texte" onClick={onFermer} className="ml-1 h-10 w-10">
            <X className="h-6 w-6" />
          </BoutonRond>
        </div>
      </div>

      {/* La progression dans le texte */}
      <div className="h-1 bg-slate-800" aria-hidden="true">
        <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 transition-all duration-300" style={{ width: `${(100 * page) / total}%` }} />
      </div>
      {lectureImposee ? (
        <p className="bg-amber-500/10 px-4 py-2 text-center text-sm text-amber-200">
          Lis le texte en entier, puis réponds aux questions. Tu pourras le relire à tout moment avec le livre, en haut de l&apos;écran.
        </p>
      ) : null}

      {/* La page, entre les deux flèches ; seul ascenseur de la lecture */}
      <div className="relative flex min-h-0 flex-1 justify-center px-2 py-4 sm:px-20 sm:py-6">
        <BoutonRond
          label="Page d'avant"
          onClick={precedente}
          disabled={page === 1}
          className="absolute left-1 top-1/2 z-10 h-11 w-11 -translate-y-1/2 bg-white/10 sm:left-5 sm:h-14 sm:w-14"
        >
          <ChevronLeft className="h-7 w-7" />
        </BoutonRond>

        <article
          ref={zonePage}
          onTouchStart={debutToucher}
          onTouchEnd={finToucher}
          className={`w-full max-w-3xl overflow-y-auto rounded-lg bg-[#fbf7ee] text-stone-800 shadow-2xl shadow-black/50 transition-all duration-300 ease-out motion-reduce:transition-none ${entree ? 'translate-x-0 opacity-100' : 'translate-x-3 opacity-0'}`}
        >
          <div className={`mx-auto max-w-[66ch] px-6 py-8 sm:px-12 sm:py-12 leading-[1.8] ${TAILLES[taille]} ${documentaire ? 'font-sans' : 'font-serif'}`}>
            {!donnee ? (
              <p className="text-center text-stone-500">Ce texte n&apos;est pas disponible.</p>
            ) : (
              <>
                {/* Le surtitre : le genre du texte, ou la grande partie d'un document (« La disparition des abeilles ») */}
                {donnee.surtitre ? (
                  <p className="mb-2 text-center text-[0.72em] uppercase tracking-[0.16em] text-stone-500">{donnee.surtitre}</p>
                ) : null}
                {donnee.title ? (
                  <h3 className={`mb-6 text-center text-[1.15em] font-semibold text-stone-900 ${documentaire ? '' : 'italic'}`}>{donnee.title}</h3>
                ) : null}
                {donnee.content === 'image' ? (
                  <figure className="space-y-2">
                    {image({ src: donnee.imageUrl, alt: donnee.title }, 'page')}
                    {donnee.caption ? <figcaption className="text-center text-[0.8em] italic text-stone-600">{donnee.caption}</figcaption> : null}
                  </figure>
                ) : (
                  <div className="whitespace-pre-line">{donnee.content}</div>
                )}
                {donnee.images?.length ? (
                  <figure className="mt-8 space-y-2">
                    <div className={`grid items-center gap-3 ${donnee.images.length > 1 ? 'sm:grid-cols-2' : ''}`}>
                      {donnee.images.map((img, i) => image(img, i))}
                    </div>
                    {donnee.legende ? <figcaption className="text-center text-[0.8em] italic text-stone-600">{donnee.legende}</figcaption> : null}
                  </figure>
                ) : null}
                {donnee.source ? <p className="mt-6 text-right text-[0.75em] italic text-stone-500">{donnee.source}</p> : null}
              </>
            )}
          </div>
        </article>

        <BoutonRond
          label="Page d'après"
          onClick={suivante}
          disabled={page === total}
          className="absolute right-1 top-1/2 z-10 h-11 w-11 -translate-y-1/2 bg-white/10 sm:right-5 sm:h-14 sm:w-14"
        >
          <ChevronRight className="h-7 w-7" />
        </BoutonRond>
      </div>

      {/* En bas : où on en est, et la suite */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 px-4 py-3 sm:px-6">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            {pages.map((_, i) => (
              <button
                key={`page-${i + 1}`}
                type="button"
                aria-label={`Aller à la page ${i + 1}`}
                aria-current={i + 1 === page ? 'page' : undefined}
                onClick={() => setPage(i + 1)}
                className={`h-2.5 rounded-full transition-all ${i + 1 === page ? 'w-6 bg-cyan-400' : 'w-2.5 bg-slate-600 hover:bg-slate-400'}`}
              />
            ))}
          </div>
          <span className="text-sm tabular-nums text-slate-400">Page {page} sur {total}</span>
        </div>
        <button
          type="button"
          onClick={bouton.action}
          className="inline-flex items-center gap-2 rounded-xl bg-blue-500 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-blue-600 sm:text-base"
        >
          {bouton.libelle}
          {bouton.fleche ? <ChevronRight className="h-5 w-5" /> : null}
        </button>
      </div>

      {/* Une image agrandie : elle tient dans l'écran, un clic ou Échap la referme */}
      {agrandie ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4 sm:p-8"
          role="dialog"
          aria-label="Image agrandie"
          onClick={() => setAgrandie(null)}
        >
          <img src={agrandie.src} alt={agrandie.alt || ''} className="max-h-full max-w-full rounded bg-white object-contain" />
          <BoutonRond label="Fermer l'image" onClick={() => setAgrandie(null)} className="absolute right-4 top-4 h-11 w-11 bg-white/10">
            <X className="h-6 w-6" />
          </BoutonRond>
        </div>
      ) : null}
    </div>
  );
};

export default LivreImmersif;
